import {themeQuartz, iconSetQuartzLight, AllCommunityModule, ModuleRegistry} from 'ag-grid-community';
import aggridUtils from "./aggrid-utils.js";

ModuleRegistry.registerModules([AllCommunityModule]);

const AgGridUtils = {
    generalTheme: (customParams) => {
        const nested = customParams?.nested;
        return themeQuartz
            .withPart(iconSetQuartzLight)
            .withParams({
                backgroundColor: "#ffffff",
                browserColorScheme: "light",
                columnBorder: true,
                fontFamily: {
                    googleFont: "Roboto, sans-serif"
                },
                foregroundColor: "rgb(46, 55, 66)",
                headerBackgroundColor: nested ? "#f7f7f7" : "#efefef",
                headerFontSize: 14,
                headerFontWeight: 700,
                headerTextColor: customParams?.headerTextColor || "#676a6c",
                oddRowBackgroundColor: customParams?.noOddRow ? "#ffffff" : "#efefef",
                rowBorder: true,
                sidePanelBorder: false,
                spacing: 8,
                wrapperBorder: !nested,
                wrapperBorderRadius: nested ? 0 : 8,
                borderRadius: nested ? 0 : 8,
                headerRowBorder: true,
                cellHorizontalPaddingScale: 0.5,
                headerVerticalPaddingScale: 0.5,
                rowVerticalPaddingScale: 0.8,
                selectedRowBackgroundColor: "#91b9eb",
                inputBorderRadius: 6,
                checkboxBorderRadius: 4,
                checkboxCheckedBackgroundColor: "#2196f3",
                headerHeight: customParams?.headerHeight || 40,
                rowHeight: customParams?.rowHeight || 31,
                // headerColumnResizeHandleHeight: "100%",

                //accentColor: customParams?.noHover ? "transparent" : "#2196f3",
            });
    },

    isEmptyForAGGrid: (val) => {
        if (val === null || val === undefined) return true;

        if (typeof val === 'number') return isNaN(val);

        if (typeof val === 'boolean') return false;

        if (typeof val === 'string') return val.trim() === '' || val.trim() === '[]';

        if (Array.isArray(val)) return val.length === 0;

        if (typeof val === 'object') return Object.keys(val).length === 0;

        return false;
    },

    nullLastComparator: (a, b, nodeA, nodeB, isInverted) => {
        const aEmpty = aggridUtils.isEmptyForAGGrid(a);
        const bEmpty = aggridUtils.isEmptyForAGGrid(b);

        if (aEmpty && bEmpty) return 0;
        if (aEmpty) return isInverted ? -1 : 1;
        if (bEmpty) return isInverted ? 1 : -1;

        if (a === b) return 0;
        if (typeof a === 'string' && typeof b === 'string') {
            return a.localeCompare(b);
        }
        return (a > b) ? 1 : -1;
    },

    sortColumns: (columns, order) => {
        return columns.sort((a, b) => {
            const indexA = order.indexOf(a.field);
            const indexB = order.indexOf(b.field);

            const adjustedIndexA = indexA === -1 ? Infinity : indexA;
            const adjustedIndexB = indexB === -1 ? Infinity : indexB;

            return adjustedIndexA - adjustedIndexB;
        });
    },

    agGridNumberYearComparator: (a, b, nodeA, nodeB, isInverted) => {
        if (a === b) return 0;

        if (aggridUtils.isEmptyForAGGrid(a) || aggridUtils.isEmptyForAGGrid(b)) {
            return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
        }

        // Handles "number[+letter]/year" identifiers, including legacy ones like "2A/2025" (number 2, text "A", year 2025)
        const parse = (value) => {
            const [head = "", year = ""] = String(value).split('/');
            return {
                year: parseInt(year.replace(/\D/g, ''), 10),
                number: parseInt(head.replace(/\D/g, ''), 10),
                text: head.replace(/[0-9]/g, ''),
            };
        };

        const compareNumber = (x, y) => {
            const xEmpty = Number.isNaN(x);
            const yEmpty = Number.isNaN(y);
            if (xEmpty && yEmpty) return 0;
            if (xEmpty) return 1;
            if (yEmpty) return -1;
            return x - y;
        };

        const pa = parse(a);
        const pb = parse(b);

        return compareNumber(pa.year, pb.year)
            || compareNumber(pa.number, pb.number)
            || pa.text.localeCompare(pb.text)
            || String(a).localeCompare(String(b));
    },

    agGridCompareDateTime: (a, b, nodeA, nodeB, isDescending) => {
        const firstDate = formatToCompareDate(a);
        const secondDate = formatToCompareDate(b);

        if (firstDate === secondDate) {
            return 0;
        }
        else if (firstDate === null) {
            return isDescending ? -1: 1;
        }
        else if (secondDate === null) {
            return isDescending ? 1: -1;
        }
        else {
            if (firstDate === secondDate) return 0;

            return firstDate - secondDate;
        }
    },

    agGridCompareDate: (a, b, nodeA, nodeB, isDescending) => {
        const firstDate = formatBrDateToTime(a);
        const secondDate = formatBrDateToTime(b);

        if (firstDate === secondDate) return 0;
        if (firstDate === null) return isDescending ? -1 : 1;
        if (secondDate === null) return isDescending ? 1 : -1;
        return firstDate - secondDate;
    },

    agGridSimpleDateComparator: (a, b, nodeA, nodeB, isDescending) => {
        if (a === b) return 0;
        if (a == null) return isDescending ? -1 : 1;
        if (b == null) return isDescending ? 1 : -1;
        const timeA = new Date(a).getTime();
        const timeB = new Date(b).getTime();
        return timeA - timeB;
    },

    localization: () => {
        return {
            page: 'Página',
            more: 'Mais',
            to: '-',
            of: 'de',
            next: 'Próximo',
            last: 'Último',
            first: 'Primeiro',
            previous: 'Anterior',
            loadingOoo: 'Carregando...',
            pageSizeSelectorLabel: 'Resultados por página',
            // for set filter
            selectAll: 'Selecionar Tudo',
            searchOoo: 'Buscar...',
            blanks: 'Vazios',
            // for number filter and text filter
            filterOoo: 'Filtrar...',
            applyFilter: 'Aplicar Filtro...',
            // for number filter
            equals: 'Igual a',
            notEqual: 'Diferente de',
            lessThan: 'Menor que',
            greaterThan: 'Maior que',
            // for text filter
            contains: 'Contém',
            notContains: 'Não Contém',
            startsWith: 'Começa Com',
            endsWith: 'Termina Com',
            // the header of the default group column
            group: 'Grupo',
            // tool panel
            columns: 'Colunas',
            // other
            noRowsToShow: 'Sem dados para exibir',
            // enterprise menu
            pinColumn: 'Fixar Coluna',
            valueAggregation: 'Agregação de Valor',
            autosizeThiscolumn: 'Tamanho automático nesta coluna',
            autosizeAllColumns: 'Tamanho automático em todas as colunas',
            groupBy: 'Agrupar por',
            ungroupBy: 'Desagrupar por',
            resetColumns: 'Resetar Colunas',
            expandAll: 'Expandir Todos',
            collapseAll: 'Recolher Todos',
            toolPanel: 'Painel de Ferramentas',
            export: 'Exportar',
            csvExport: 'Exportar para CSV',
            excelExport: 'Exportar para Excel',
            pdfExport: 'Exportar para PDF',
            excelXmlExport: 'Exportar para Excel (.xml)',
            // enterprise menu pinning
            pinLeft: 'Fixar à esquerda',
            pinRight: 'Fixar à direita',
            // enterprise menu aggregation and status panel
            sum: 'Somar',
            min: 'Mínimo',
            max: 'Máximo',
            none: 'Nenhum',
            count: 'Contagem',
            average: 'Média',
            // standard menu
            copy: 'Copiar',
            copyWithHeaders: 'Copiar com cabeçalhos',
            ctrlC: 'ctrl + C',
            paste: 'Colar',
            ctrlV: 'ctrl + V'
        }
    },

    defaultGridOptions: () => {
        return {
            localeText: AgGridUtils.localization(),
            paginationNumberFormatter: (params) => {
                return params.value.toLocaleString('pt-BR');
            }
        }
    },

    filterParams: {
        textFormatter: (r) => {
            if (r == null) return null;
            return r
                .toLowerCase()
                .replace(/[àáâãäå]/g, "a")
                .replace(/æ/g, "ae")
                .replace(/ç/g, "c")
                .replace(/[èéêë]/g, "e")
                .replace(/[ìíîï]/g, "i")
                .replace(/ñ/g, "n")
                .replace(/[òóôõö]/g, "o")
                .replace(/œ/g, "oe")
                .replace(/[ùúûü]/g, "u")
                .replace(/[ýÿ]/g, "y");
        }, debounceMs: 200, maxNumConditions: 1,
    },

    lowerUpCaseComparator: (valueA, valueB) => {
        const normalizeA = (valueA ?? "").toString().trim();
        const normalizeB = (valueB ?? "").toString().trim();
        return normalizeA.localeCompare(normalizeB, "pt-BR", { sensitivity: "base" });
    },

    filterChangedNoRowsOverlay: ({ api }) => api.getDisplayedRowCount() ? api.hideOverlay() : api.showNoRowsOverlay(),

}

const formatToCompareDate = (givenDate) => {
    if (!givenDate || typeof givenDate !== 'string' || givenDate.trim() === '') {
        return null;
    }
    const [datePart, timePart = "00:00"] = givenDate.split(' ');
    const [day, month, year] = datePart.split('/');
    const [hour = "00", minute = "00", second = "00"] = timePart.split(':');
    const dateObj = new Date(`${year}-${month}-${day} ${hour}:${minute}:${second}`);

    return dateObj.getTime();
};

const formatBrDateToTime = (givenDate) => {
    if (!givenDate || typeof givenDate !== 'string' || givenDate.trim() === '') {
        return null;
    }
    const [datePart, timePart = "00:00:00"] = givenDate.trim().split(' ');
    const [day, month, year] = datePart.split('/').map(Number);
    const [hour = 0, minute = 0, second = 0] = timePart.split(':').map(Number);
    const time = new Date(year, month - 1, day, hour, minute, second).getTime();

    return Number.isNaN(time) ? null : time;
};

export default AgGridUtils;
